/**
 * Browser Back Button Protection Mixin
 *
 * Provides global protection against browser back button navigation.
 * Shows warning dialog when users attempt to use browser back button.
 *
 * IMPORTANT: This approach uses history manipulation to prevent
 * the browser back navigation from actually occurring, then shows
 * the dialog to ask the user what they want to do.
 */

// Global state for browser back protection
const globalProtectionState = {
  isEnabled: true,
  dialogShown: false,
  isInitialized: false,
  currentUrl: '',
}

/**
 * Initialize global browser back protection
 * This must be called early in the application lifecycle
 */
function initializeGlobalProtection() {
  if (globalProtectionState.isInitialized || typeof window === 'undefined') {
    return
  }

  console.debug('Initializing global browser back protection...')

  // Store current URL
  globalProtectionState.currentUrl = window.location.href

  // Add popstate listener
  window.addEventListener('popstate', handleGlobalPopState)

  // Add initial protected state to history
  if (window.history && window.history.pushState) {
    window.history.pushState(
      { letsProtected: true, timestamp: Date.now() },
      '',
      window.location.href
    )
  }

  globalProtectionState.isInitialized = true
  console.debug('Global browser back protection initialized')
}

/**
 * Handle global popstate events (browser back/forward buttons)
 */
async function handleGlobalPopState(event) {
  console.debug('Global popstate detected:', {
    event,
    state: event.state,
    currentUrl: window.location.href,
    dialogShown: globalProtectionState.dialogShown,
    isEnabled: globalProtectionState.isEnabled
  })

  // Skip if protection is disabled or dialog already shown
  if (!globalProtectionState.isEnabled || globalProtectionState.dialogShown) {
    console.debug('Protection skipped - disabled or dialog already shown')
    return
  }

  // Get Vue instance
  const vueInstance = window.vue
  if (!vueInstance) {
    console.debug('Vue instance not available')
    return
  }

  console.warn('Using this.$router.back() will not work as expected due to browser back protection.\n Please use named routes for navigation instead.')

  // Check if current route should be protected
  const currentRouteName = vueInstance.$route?.name
  const excludedRoutes = ['Login', 'Error', 'NotFound', 'Logout']

  if (excludedRoutes.includes(currentRouteName)) {
    console.debug('Route excluded from protection:', currentRouteName)
    return
  }

  // IMMEDIATELY push state back to prevent navigation
  if (window.history && window.history.pushState) {
    window.history.pushState(
      { letsProtected: true, timestamp: Date.now() },
      '',
      window.location.href
    )
    console.debug('Navigation prevented by pushing state back')
  }

  // Mark that dialog is being shown
  globalProtectionState.dialogShown = true

  try {
    console.debug('Showing browser back confirmation diaconsole...')

    // Show confirmation dialog using Vue instance
    const result = await vueInstance.$confirm({
      title: '確認',
      text: 'ブラウザの「戻る」を操作すると正しく機能しない可能性があります。処理を継続しますか？',
      persistent: true,
      actions: [
        { key: 'navigate', text: '移動', color: 'primary' },
        {
          key: 'cancel',
          text: 'キャンセル',
          color: '#AEAEAE',
          props: {
            style: {
              color: '#FFFFFF'
            }
          }
        },
      ]
    })

    console.debug('Dialog result:', result)

    if (result === 'navigate') {
      console.debug('User chose to navigate to TOP screen')
      // Reset state before navigation to avoid intercepting our own push
      globalProtectionState.dialogShown = false
      // SUCCESS: Using window.location.href to force navigation and clear current history state issues
      window.location.href = '/'
    } else {
      console.debug('User chose to cancel - staying on current page')
      // SUCCESS: After a "Cancel", we need to push another state to the history stack
      // to ensure the next browser back press has a state to pop from.
      if (window.history && window.history.pushState) {
        window.history.pushState(
          { letsProtected: true, timestamp: Date.now() },
          '',
          window.location.href
        )
      }
    }

  } catch (error) {
    console.error('Error showing global browser back dialog:', error)
  } finally {
    globalProtectionState.dialogShown = false
  }
}

/**
 * Update current URL when navigating (called by router)
 */
function updateCurrentUrl(url) {
  console.log(url)
  if (!url || typeof url !== 'string' || url === '/') return; // Skip invalid or root
  // Ensure url is a full URL
  if (!url.startsWith('http')) {
    url = window.location.origin + url
  }
  console.log('UTL', url)
  globalProtectionState.currentUrl = url

  // Push new protected state for the new URL
  if (window.history && window.history.pushState && globalProtectionState.isEnabled) {
    setTimeout(() => {
      window.history.pushState(
        { letsProtected: true, timestamp: Date.now() },
        '',
        url
      )
    }, 0)
  }
}

// Export for use in router
export { updateCurrentUrl, globalProtectionState }

// Initialize protection when this module loads
if (typeof window !== 'undefined') {
  // Initialize after a short delay to ensure Vue is ready
  setTimeout(initializeGlobalProtection, 100)
}

/**
 * Main mixin export
 */
export default {
  // Removed beforeRouteEnter as it doesn't work in Vue 3 compat mode
  // URL update is now handled in router afterEach
}
